import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import React__default, { useMemo, createElement } from 'react';
import { getCssBackground } from '@fabric-app/ui/theme/cssBackground';
import { mediaTablet, mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { motion } from 'framer-motion';
import { mergeRefs } from 'react-merge-refs';
import { styled, css } from 'styled-components';
import { Flex } from '@fabric-app/ui/components/Flex';
import { preventForwardPropsConfig } from '@fabric-app/ui/utils/preventForwardProps';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { useScrollPosition } from '@fabric-app/hooks/useScrollPosition';

const dashboardContentPadding = {
  desktop: "3.125rem",
  tablet: "1.875rem",
  mobile: ".75rem"
};
const ContentInner = styled(motion(Flex)).withConfig(preventForwardPropsConfig([
  "maxContentSize",
  "disablePadding",
  "disableSidePadding"
])).attrs((props) => {
  return {
    direction: "column",
    gap: "sections",
    alignItems: "stretch",
    ...props
  };
}).withConfig({
  displayName: "DashboardContent__ContentInner",
  componentId: "sc-697222d-0"
})([
  `margin-left:auto;margin-right:auto;min-height:100%;padding:`,
  `;padding-bottom:calc( var(--safe-margin-offset-bottom) + `,
  ` / 2 + `,
  ` );`,
  `{padding:`,
  `;padding-bottom:calc( var(--safe-margin-offset-bottom) + `,
  ` / 2 + `,
  ` );}`,
  `{padding:`,
  `;padding-bottom:calc( var(--safe-margin-offset-bottom) + 12rem + `,
  ` / 2 + `,
  ` );width:100dvw;}`,
  ` `,
  ``
], ({ padding, disablePadding }) => disablePadding ? "0px" : padding?.desktop ?? dashboardContentPadding.desktop, ({ padding }) => padding?.desktop ?? dashboardContentPadding.desktop, (p) => p.bottomPaddingOffset || "5rem", mediaTablet, ({ padding, disablePadding }) => disablePadding ? "0px" : padding?.tablet ?? dashboardContentPadding.tablet, ({ padding }) => padding?.tablet ?? dashboardContentPadding.tablet, (p) => p.bottomPaddingOffset || "var(--footer-height, 2rem)", mediaMobile, ({ padding, disablePadding }) => disablePadding ? "0px" : padding?.mobile ?? dashboardContentPadding.mobile, ({ padding }) => padding?.mobile ?? dashboardContentPadding.mobile, (p) => p.bottomPaddingOffset || "var(--footer-height, 2rem)", (p) => {
  switch (p.maxContentSize) {
    case "sm":
      return css([
        `max-width:calc(980px + `,
        ` * 2);`,
        `{max-width:calc(980px + `,
        ` * 2);}`,
        `{max-width:calc(980px + `,
        ` * 2);}`
      ], p.padding?.desktop ?? dashboardContentPadding.desktop, mediaTablet, p.padding?.tablet ?? dashboardContentPadding.tablet, mediaMobile, p.padding?.mobile ?? dashboardContentPadding.mobile);
    case "medium":
      return css([
        `max-width:calc(1248px + `,
        ` * 2);`,
        `{max-width:calc(1248px + `,
        ` * 2);}`,
        `{max-width:calc(1248px + `,
        ` * 2);}`
      ], p.padding?.desktop ?? dashboardContentPadding.desktop, mediaTablet, p.padding?.tablet ?? dashboardContentPadding.tablet, mediaMobile, p.padding?.mobile ?? dashboardContentPadding.mobile);
    case "xs":
      return css([
        `max-width:calc(500px + `,
        ` * 2);`,
        `{max-width:calc(500px + `,
        ` * 2);}`,
        `{max-width:calc(500px + `,
        ` * 2);}`
      ], p.padding?.desktop ?? dashboardContentPadding.desktop, mediaTablet, p.padding?.tablet ?? dashboardContentPadding.tablet, mediaMobile, p.padding?.mobile ?? dashboardContentPadding.mobile);
  }
}, (p) => p.disableSidePadding && css([
  `padding-left:0 !important;padding-right:0 !important;`
]));
const DashboardContentSection = styled.div.withConfig({
  displayName: "DashboardContent__DashboardContentSection",
  componentId: "sc-697222d-1"
})([
  `padding:0 `,
  `;`,
  `{padding:0 `,
  `;}`,
  `{padding:0 `,
  `;}`
], dashboardContentPadding.desktop, mediaTablet, dashboardContentPadding.tablet, mediaMobile, dashboardContentPadding.mobile);
const DashboardContent = styled(ScrollArea.Root).attrs(({ maxHeightAndCenterContent, innerProps, viewportRef: viewportRefProp = null, expandVertically = true, scrollBarProps, preChildren, ...props }) => {
  const viewportRef = React__default.useRef(null);
  const mergedViewportRef = useMemo(() => mergeRefs([
    viewportRef,
    viewportRefProp
  ]), [
    viewportRef,
    viewportRefProp
  ]);
  const [scrollPosition] = useScrollPosition(viewportRef.current);
  return {
    grow: true,
    ...props,
    orientation: "both",
    "data-scroll-vertical": scrollPosition?.vertical,
    "data-scroll-horizontal": scrollPosition?.horizontal,
    children: /* @__PURE__ */ jsxs(Fragment, {
      children: [
        /* @__PURE__ */ jsxs(ScrollArea.Viewport, {
          ref: mergedViewportRef,
          style: maxHeightAndCenterContent ? {
            height: "auto"
          } : void 0,
          expandVertically,
          children: [
            preChildren,
            /* @__PURE__ */ createElement(ContentInner, {
              "data-dashboard-content-inner": true,
              ...innerProps,
              key: innerProps?.key
            }, props.children)
          ]
        }),
        /* @__PURE__ */ jsx(ScrollArea.Bar, {
          orientation: "vertical",
          variant: "default",
          ...scrollBarProps,
          children: /* @__PURE__ */ jsx(ScrollArea.Thumb, {})
        }),
        /* @__PURE__ */ jsx(ScrollArea.Bar, {
          orientation: "horizontal",
          variant: "default",
          ...scrollBarProps,
          children: /* @__PURE__ */ jsx(ScrollArea.Thumb, {})
        }),
        /* @__PURE__ */ jsx(ScrollArea.Corner, {})
      ]
    })
  };
}).withConfig(preventForwardPropsConfig([
  "maxHeightAndCenterContent",
  "maxContentSize",
  "innerProps",
  "background",
  "viewportRef",
  "scrollBarProps"
])).withConfig({
  displayName: "DashboardContent",
  componentId: "sc-697222d-2"
})([
  `&>[data-radix-scroll-area-viewport]>div{display:block !important;}&>[data-radix-scroll-area-viewport] > div{display:block !important;}`,
  ` `,
  `{`,
  `}`,
  ``
], getCssBackground("secondary"), mediaMobile, getCssBackground("primary"), (p) => p.maxHeightAndCenterContent && css([
  `display:flex;flex-direction:column;flex-grow:1;justify-content:center;`
]));
const EmptyContainerInner = styled(Flex).attrs((props) => {
  return {
    direction: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "sections",
    ...props
  };
}).withConfig({
  displayName: "DashboardContent__EmptyContainerInner",
  componentId: "sc-697222d-3"
})([
  `max-width:560px;text-align:center;`
]);
const DashboardContentEmptyContainer = styled(motion(Flex)).attrs((props) => {
  return {
    direction: "column",
    alignItems: "center",
    justifyContent: "center",
    ...props,
    children: /* @__PURE__ */ jsx(EmptyContainerInner, {
      children: props.children
    })
  };
}).withConfig(preventForwardPropsConfig([
  "background"
])).withConfig({
  displayName: "DashboardContent__DashboardContentEmptyContainer",
  componentId: "sc-697222d-4"
})([
  `min-height:50vh;`,
  ``
], getCssBackground("transparent"));

export { DashboardContent, DashboardContentEmptyContainer, DashboardContentSection, dashboardContentPadding };
//# sourceMappingURL=DashboardContent.js.map
